import { passRxMidiData } from "../../main2worker"
import { checkEnhancedMidi } from "./enhancedmidi"
import * as SoftSynth from "./softsynth"

let useSoftSynth = true
const DEBUG = false

let midiAccess: MIDIAccess | undefined
let midiAccessRequest: Promise<boolean> | undefined

const midiReady = (midi: MIDIAccess) => {
  // Also react to device changes.
  midiAccess = midi
  midi.addEventListener("statechange", () => initDevices())
  initDevices()
}

let midiInIndex  = -1
let midiOutIndex = -1
export let midiInDevices: MIDIInput[] = []
export let midiOutDevices: MIDIOutput[] = []

export const isWebMidiSupported = () => {
  return typeof navigator !== "undefined" && !!navigator.requestMIDIAccess
}

export const hasWebMidiAccess = () => {
  return midiAccess !== undefined
}

export const requestWebMidiAccess = async (): Promise<boolean> => {
  if (midiAccess)
    return true

  if (!isWebMidiSupported()) {
    console.log("Web MIDI not supported")
    return false
  }

  if (midiAccessRequest)
    return midiAccessRequest

  console.log("Requesting MIDI access")
  midiAccessRequest = navigator.requestMIDIAccess({ sysex: true })
    .then((midi) => {
      midiReady(midi)
      return true
    })
    .catch((err) => {
      console.log("requestMIDIAccess fails", err)
      return false
    })

  try {
    return await midiAccessRequest
  } finally {
    midiAccessRequest = undefined
  }
}

export const getMidiOutIndex = (): number => {
  return midiOutIndex
}

export const setMidiOutIndex = (index: number) => {
  if (index < -1 || index >= midiOutDevices.length) {
    console.warn(`Cannot select MIDI output index ${index}; selection unchanged.`)
    return false
  }

  if (midiOutIndex != index)
  {
    midiOutIndex = index
    if (midiOutIndex != -1)
      console.log("Selecting MIDI output: " + midiOutDevices[midiOutIndex].name)
  }
  return true
}

export const getMidiInIndex = (): number => {
  return midiInIndex
}

export const setMidiInIndex = (index: number) => {
  if (midiInIndex != index)
  {
    midiInIndex = index
    console.log("Selecting MidiIn Device: " + midiInDevices[midiInIndex].name)
    const device = midiInDevices[midiInIndex]
    device.addEventListener("midimessage", midiMessageReceived)
  }
}

const initDevices = () => {

  const midi = midiAccess

  if (!midi)
    return

  const selectedOutput = midiOutDevices[midiOutIndex]
  const selectedOutputId = selectedOutput?.id
  const fallbackToBuiltInSynth = () => {
    midiOutIndex = -1
    if (!useSoftSynth) {
      const outputName = selectedOutput?.name || "Unknown MIDI Output"
      if (SoftSynth.isSoftSynthAvailable()) {
        console.warn(`MIDI output "${outputName}" is no longer available; using the built-in synthesizer.`)
        enableSoftSynth()
      } else {
        console.warn(`MIDI output "${outputName}" is no longer available; no MIDI output is selected.`)
      }
    }
  }

  // Reset.
  midiInDevices = []
  midiOutDevices = []

  const outputs = midi.outputs.values()
  for (let output = outputs.next(); output && !output.done; output = outputs.next()) {
    if (output.value.state !== "disconnected")
      midiOutDevices.push(output.value)
  }

  if (midiOutDevices.length)
  {
    if (selectedOutputId !== undefined)
    {
      midiOutIndex = midiOutDevices.findIndex((device) => device.id === selectedOutputId)
      if (midiOutIndex === -1)
        fallbackToBuiltInSynth()
    }
    else
    {
      midiOutIndex = 0
      const device = midiOutDevices[midiOutIndex]
      console.log("Selecting MIDI output: " + device.name)
    }
  }
  else
  {
    fallbackToBuiltInSynth()
  }
  
  const inputs = midi.inputs.values()
  for (let input = inputs.next(); input && !input.done; input = inputs.next()) {
    console.log("Midi In: " + input.value.name)
    midiInDevices.push(input.value)
  }

  // don't listen on midi-through if we are sending on midi-through
  // changed >1 to >0 in order to enable Midi IN
  if (midiInDevices.length > 0)
  {
    // pick last one
    if (midiInIndex != midiInDevices.length-1)
    {
      midiInIndex = midiInDevices.length-1
      const device = midiInDevices[midiInIndex]
      device.addEventListener("midimessage", midiMessageReceived)
      console.log("Selecting MidiInDevice: " + device.name)
    }
  }
  else
    midiInIndex = -1
}

const midiMessageReceived = (event: MIDIMessageEvent) => {
  if (!event.data) return
  const data = new Uint8Array(event.data)
  if (DEBUG)
  {
    let txt = "Recv: [" + data[0].toString(16)
    for(let i=1;i<data.length;i++)
      txt += (" " + data[i].toString(16))
    txt += "]"
    console.log(txt)
  }
  passRxMidiData(data)
}

// probably not necessary
const doActiveSense = false
let itimer: ReturnType<typeof setInterval> | number = 0
const activeSense = () => {
  const data = new Uint8Array(1).fill(0xFE)
  passRxMidiData(data)
}

const parseAndSendMsg = (msg: number[]) => {
  // Determine the output device (Web MIDI or built-in synth)
  const device = (useSoftSynth || midiOutIndex === -1) 
    ? SoftSynth as unknown as MIDIOutput  // Software synth has compatible send() API
    : midiOutDevices[midiOutIndex]

  // start active sensing every ~250ms once we send an initial message
  if (doActiveSense && itimer === 0)
    itimer = setInterval(activeSense, 250)

  if (DEBUG)
  {
    const hexString = msg.map(x => ("00" + x.toString(16)).slice(-2)).join(" ")
    console.log("Msg: ", hexString)
  }

  // Check enhanced MIDI first with appropriate device
  if (checkEnhancedMidi(msg, device))
    return

  // If not enhanced MIDI, send directly
  device.send(msg)
}

enum State
{
  COMMAND,
  ARGS2,
  ARGS1,
  SYSEX
}

let once = false
const msg: number[] = []
const rtMsg: number[] = []
let state : State = State.COMMAND

// initial value is used to detect running status error when status byte not received yet
const noStsSeenYet: number = 0xFF
let prevStatus: number = noStsSeenYet

export const receiveMidiData = (data: Uint8Array) => {
  // Fall back to the built-in synth if no external output is selected.
  if (midiOutIndex === -1 && !useSoftSynth && SoftSynth.isSoftSynthAvailable()) {
    console.log("No MIDI interface detected. Using built-in software synthesizer.")
    SoftSynth.initSoftSynth()
    useSoftSynth = true
  }

  if (midiOutIndex === -1 && !useSoftSynth) {
    // connection failure and no soft synth
    if (once) {
      console.log("No MIDI interface and software synth unavailable.")
      once = false
    }
    return
  }

  // Must parse the message, as WebMIDI only accepts complete messages.
  for(let i=0;i<data.length;i++)
  {
    const byte = data[i]

    //msg.push moved to state machine logic below in order to handle Running Status

    switch(state)
    {
      case State.ARGS2:
        // check for realtime message
        if ((byte & 0xF8) == 0xF8)
        {
          rtMsg.push(byte)
          msg.pop()
        }
        else
          if ((byte & 0x80) != 0x80)
          {
            state = State.ARGS1
            if (prevStatus != noStsSeenYet)
              msg.push(byte)
          } else
          {
            state = State.COMMAND
            msg.length = 0 // out of alignment before current message complete
          }
        break

      case State.ARGS1:
        // check for realtime message
        if ((byte & 0xF8) == 0xF8)
        {
          rtMsg.push(byte)
          msg.pop()
        }
        else
        {
          state = State.COMMAND
          if (prevStatus != noStsSeenYet)
            msg.push(byte)
        }
        break

      case State.SYSEX:
        if (DEBUG)
          console.log("MIDI: SYSEX: ", byte.toString(16))
        if (byte === 0xF7)
          state = State.COMMAND
        msg.push(byte)
        break
        
      case State.COMMAND:
        if ((byte & 0x80) != 0x80) // handle running status - reuse previous status byte
        {
          if (prevStatus != noStsSeenYet)
          {
            msg.push(prevStatus)
            msg.push(byte)
            if (((prevStatus & 0xF0) == 0xC0) || ((prevStatus & 0xF0) == 0xD0))
            {
              state = State.COMMAND   // messages Cx and Dx have one data byte
            }
            else
            {
              state = State.ARGS1 // message has 2 data bytes
            }
          }
        }
        else
        {
          switch(byte & 0xF0)
          {
            case 0x80:
            case 0x90:
            case 0xA0:
            case 0xB0:
            case 0xE0:
              state = State.ARGS2
              msg.push(byte)
              prevStatus = byte
              break
            
            case 0xC0:
            case 0xD0:
              state = State.ARGS1
              msg.push(byte)
              prevStatus = byte
              break

            case 0xF0:
              switch(byte)
              {
                case 0xF0:
                  state = State.SYSEX
                  if (DEBUG)
                    console.log("MIDI: SYSEX: ", byte.toString(16))
                  msg.push(byte)
                  break
                case 0xF2:
                  state = State.ARGS2
                  break
                case 0xF1:
                case 0xF3:
                  state = State.ARGS1
                  break
                default:
                  // remain in State.COMMAND, 1 byte arg
                  break
              }
              break

            default:
              // out of alignment / bad data?
              // stay in command state
              console.log("MIDI: byte unexpected: ", byte.toString(16))
              state = State.COMMAND
              msg.length = 0
              break
          }
          break
        }
    }
    // always send interleaved realtime messages
    if (rtMsg.length > 0)
    {
      parseAndSendMsg(rtMsg)
      rtMsg.length = 0
    }

    if (state == State.COMMAND && msg.length > 0)
    {
      parseAndSendMsg(msg)
      msg.length = 0
    }
  }
}

// Public API for controlling software synthesizer
export const enableSoftSynth = () => {
  if (SoftSynth.isSoftSynthAvailable()) {
    SoftSynth.initSoftSynth()
    useSoftSynth = true
    console.log("Software synthesizer enabled")
    return true
  }
  console.warn("Software synthesizer not available in this browser")
  return false
}

export const disableSoftSynth = () => {
  if (useSoftSynth) {
    SoftSynth.stopAllNotes()
    useSoftSynth = false
    console.log("Software synthesizer disabled")
  }
}

export const isSoftSynthEnabled = () => {
  return useSoftSynth
}

export const isSoftSynthAvailable = () => {
  return SoftSynth.isSoftSynthAvailable()
}

export const setSoftSynthMasterVolume = (volume: number) => {
  SoftSynth.setMasterVolume(volume)
}
